import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { X, Lock, LogOut } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import axios from 'axios';
import { ShiftMethodSummary } from './ShiftMethodSummary';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Fechamento RÁPIDO do turno pelo PDV.
 * - Operador digita apenas a senha
 * - Backend marca sessão como pending_close (aguardando conferência)
 * - Operador é deslogado automaticamente
 * - A conferência dos valores (dinheiro/PIX/cartão/sangrias/etc.) é feita depois
 *   pelo admin/gerente em Configurações > Fluxo de Caixa > Resumo de Turnos.
 */
export const CloseShiftModal = ({ open, onClose, onClosed }) => {
  const { user, logout } = useAuth();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [endedId, setEndedId] = useState(null);

  if (!open) return null;

  const doLogout = async () => {
    try { await logout(); } catch (_) { /* ignore */ }
    window.location.href = '/login';
  };

  const closeAndReset = () => {
    setPassword('');
    setErr('');
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!password) { setErr('Digite sua senha para confirmar.'); return; }
    setLoading(true); setErr('');
    try {
      const r = await axios.post(`${API}/cash-sessions/end-shift`, {
        counted_amount: 0,
        operator_password: password,
        notes: 'Encerramento rápido pelo PDV — aguardando conferência do gerente',
      }, { withCredentials: true });
      if (onClosed) onClosed();
      // Mostra ao operador o que ele recebeu por forma de pagamento no turno; o logout fica no botão "Sair"
      setEndedId(r.data?.id || null);
      setLoading(false);
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Erro ao encerrar turno');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/70 z-[70] flex items-center justify-center p-4" data-testid="close-shift-modal">
      <div className={`bg-white rounded-md w-full ${endedId ? "max-w-xl" : "max-w-md"} flex flex-col shadow-2xl max-h-[92vh] overflow-y-auto`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-800 to-slate-900 rounded-t-md">
          <div className="flex items-center gap-2 text-white">
            <LogOut size={18} />
            <h2 className="text-sm font-black uppercase tracking-wider">Encerrar Turno</h2>
          </div>
          <button
            onClick={closeAndReset}
            className="text-white/70 hover:text-white transition-colors"
            data-testid="close-shift-close-btn"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        {endedId ? (
          <div className="p-5 space-y-4" data-testid="close-shift-summary">
            <div className="bg-emerald-50 border border-emerald-300 rounded p-3 text-sm text-emerald-900">
              <p className="font-bold">✓ Turno encerrado, {user?.name}.</p>
              <p className="text-xs mt-1">Confira abaixo o que você recebeu em cada forma de pagamento. A conferência final (contagem) é feita pelo gerente.</p>
            </div>
            <ShiftMethodSummary sessionId={endedId} />
            <div className="flex justify-end">
              <Button type="button" onClick={doLogout} className="bg-slate-800 hover:bg-black text-white font-bold" data-testid="close-shift-logout">
                <LogOut size={16} className="mr-2" /> Sair
              </Button>
            </div>
          </div>
        ) : (
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
            <p className="font-bold mb-1">📋 Encerramento rápido</p>
            <p>Ao confirmar, seu turno será encerrado e você verá o resumo do que recebeu por forma de pagamento.</p>
            <p className="mt-1">
              A <strong>conferência dos valores</strong> (dinheiro, PIX, cartão, sangrias) será feita depois pelo gerente
              em <em>Configurações → Fluxo de Caixa → Resumo de Turnos</em>.
            </p>
          </div>

          <div className="text-sm">
            <p className="text-slate-600">Operador: <strong className="text-slate-900">{user?.name}</strong></p>
          </div>

          <div>
            <Label htmlFor="close-pwd" className="text-xs uppercase tracking-wider font-bold text-slate-600">
              <Lock size={12} className="inline mr-1" />Sua senha para confirmar
            </Label>
            <Input
              id="close-pwd"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              data-testid="close-shift-password"
              placeholder="••••••••"
              className="mt-1"
              required
            />
          </div>

          {err && (
            <Alert variant="destructive">
              <AlertDescription data-testid="close-shift-error">{err}</AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2 justify-end pt-2">
            <Button type="button" variant="outline" onClick={closeAndReset} disabled={loading} data-testid="close-shift-cancel">
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-red-600 hover:bg-red-700 text-white font-bold"
              disabled={loading || !password}
              data-testid="close-shift-confirm"
            >
              <LogOut size={16} className="mr-2" />
              {loading ? 'Encerrando...' : 'Encerrar Turno'}
            </Button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};
